import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Link2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@patient-watch/ui/sonner";
import { hooks as h } from "@/hooks";
import { Alert, AlertDescription } from "@patient-watch/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@patient-watch/ui/card";
import Stepper from "@/components/common/Stepper";
import useUsersOrganisationsRolesQuery from "@/features/auth/api/selectUsersOrganisationsRoles";
import { uniqueOrganisationsFromRoles } from "@/features/auth/utils/uniqueOrganisationsFromRoles";
import {
    type DiaryInviteSearchT,
    getDiaryInviteContext,
    getDiaryInviteOrigin,
} from "@/features/invite/utils/diaryInviteContext";
import { createDiaryInvite } from "@/features/invite/api/createDiaryInvite";
import { isAtDiaryCap, useClinicianDiaryUsage } from "@/features/invite/api/useClinicianDiaryUsage";
import DiaryQuotaBlockedCallout from "@/features/invite/components/DiaryQuotaBlockedCallout";
import { InviteQrMark } from "@/features/qr-code/components/QrCodeRoute";
import { signupOrScreenerLocation } from "@/features/screeners/utils/screenerSearch";
import { normalizeTemplateSettingsSearch } from "@/features/templates/utils/normalizeTemplateSettingsSearch";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useActionCopy } from "@/features/i18n/actionCopy";
import FooterButtons from "@/components/common/FooterButtons";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { getDiaryInviteSteps, parseDiaryInviteMethod } from "@/features/invite/utils/diaryInviteSteps";
import { services as s } from "@/services";
import { type TemplateSettingsSchemaT } from "@patient-watch/types";

export const Route = createFileRoute("/_app/invite/diary/qr")({
    component: QRCodePage,
    validateSearch: (search: Record<string, unknown>) => search as DiaryInviteSearchT,
});

function QRCodePage() {
    const search = Route.useSearch();
    const { auth } = Route.useRouteContext();
    const navigate = useNavigate({ from: Route.fullPath });
    const router = useRouter();
    const qrWrapRef = useRef<HTMLDivElement>(null);
    const { locale: appLocale } = useAppLocale();
    const inviteCopy = useInviteCopy();
    const actionCopy = useActionCopy();
    const patientsCopy = usePatientsCopy();
    const templatesCopy = useTemplatesCopy();
    const toastCopy = useToastCopy();

    const origin = getDiaryInviteOrigin(search);
    const inviteMethod = parseDiaryInviteMethod(search.invite_method) ?? "qr";
    const context = getDiaryInviteContext(origin, {
        patients: patientsCopy.patients,
        templates: templatesCopy.templates,
        newDiary: patientsCopy.newDiary,
    });

    const { data: userOrgRoles = [], isLoading: isLoadingOrgs } = useUsersOrganisationsRolesQuery(auth.id);
    const organisations = useMemo(() => uniqueOrganisationsFromRoles(userOrgRoles), [userOrgRoles]);

    h.useNav([
        { label: context.rootLabel, route: { to: context.rootTo } },
        { label: context.flowLabel, route: { to: "/invite/diary/review", search } },
        { label: inviteCopy.organisationAndContact, route: { to: "/invite/diary/organisation", search } },
        { label: inviteCopy.shareQrCode, route: { to: "/invite/diary/qr", search } },
    ]);

    const { data: diaryUsage } = useClinicianDiaryUsage(auth.id);
    const atDiaryCap = isAtDiaryCap(diaryUsage);

    const hasTemplateSettings = search && (Boolean(search.trackers?.length) || Boolean(search.required_events?.length));
    const organisation_id =
        typeof search.organisation_id === "string" && search.organisation_id.trim() !== ""
            ? search.organisation_id.trim()
            : "";
    const organisation_name =
        typeof search.organisation_name === "string" && search.organisation_name.trim() !== ""
            ? search.organisation_name.trim()
            : (organisations.find((org) => org.organisation_id === organisation_id)?.organisation_name ?? "");
    const contact_id =
        typeof search.contact_id === "string" && search.contact_id.trim() !== "" ? search.contact_id.trim() : undefined;
    const hospital_number =
        typeof search.hospital_number === "string" && search.hospital_number.trim() !== ""
            ? search.hospital_number.trim()
            : undefined;
    const inviteLocale = search.locale ?? appLocale;
    const hasOrganisationId = organisation_id.length > 0;
    const hasContactId = Boolean(contact_id);

    const [quickstartId] = useState(() => s.uuidv4());

    /** Stable template snapshot for diary_invites - excludes dynamic org/hospital/contact overrides. */
    const settingsSnapshot = useMemo((): TemplateSettingsSchemaT | null => {
        if (!hasTemplateSettings) return null;
        const {
            organisation_id: _organisationId,
            organisation_name: _organisationName,
            hospital_number: _hospitalNumber,
            contact_id: _contactId,
            ...templatePayload
        } = search;
        return normalizeTemplateSettingsSearch({
            ...templatePayload,
            invited_by: search.invited_by ?? auth.id,
            locale: search.locale ?? inviteLocale,
        });
        // Intentionally omit org / hospital / contact so URL-only edits do not recreate the row.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- stable bulky payload from search
    }, [
        hasTemplateSettings,
        auth.id,
        inviteLocale,
        search.trackers,
        search.consents,
        search.screener_id,
        search.required_events,
        search.diary_labels,
        search.injection_details,
        search.product_option_id,
        search.provided_by,
        search.surgery_option_id,
        search.orthopaedic_area,
        search.quickstart_key,
        search.require_hospital_number,
        search.reference_date_mode,
        search.invited_by,
        search.name,
        search.description,
        search.injecting_clinician_id,
        search.referring_clinician_id,
        search.locale,
    ]);

    const {
        mutateAsync: createInvite,
        data: diaryInvite,
        isPending: isCreatingInvite,
        isError: isInviteError,
        reset: resetInvite,
    } = useMutation({
        mutationFn: createDiaryInvite,
    });

    useEffect(() => {
        if (!settingsSnapshot || atDiaryCap || !hasOrganisationId) return;

        resetInvite();
        void createInvite({
            settings: settingsSnapshot,
            invitedBy: auth.id,
            organisationId: organisation_id,
        }).catch((error) => {
            toast.error(error instanceof Error ? error.message : inviteCopy.failedToCreateInviteLink);
        });
    }, [
        settingsSnapshot,
        atDiaryCap,
        auth.id,
        organisation_id,
        hasOrganisationId,
        createInvite,
        resetInvite,
        inviteCopy.failedToCreateInviteLink,
    ]);

    const invitedBy =
        (typeof search.invited_by === "string" && search.invited_by.trim() !== "" ? search.invited_by : auth.id) ||
        auth.id;

    const destinationUrl =
        hasOrganisationId && hasContactId && diaryInvite?.diary_invite_id && typeof window !== "undefined"
            ? `${window.location.origin}${
                  router.buildLocation(
                      signupOrScreenerLocation(
                          {
                              diary_invite_id: diaryInvite.diary_invite_id,
                              role: "PATIENT",
                              organisation_id: organisation_id,
                              organisation_name: organisation_name || undefined,
                              organisation: {
                                  organisation_id: organisation_id,
                                  organisation_name: organisation_name,
                              },
                              invited_by: invitedBy,
                              contact_id,
                              hospital_number,
                              locale: inviteLocale,
                              quickstart_key: search.quickstart_key?.toUpperCase() ?? "PAIN",
                              quickstart_id: quickstartId,
                          } as Record<string, unknown>,
                          search.screener_id
                      ) as never
                  ).href
              }`
            : "";

    const copyLink = (event?: React.MouseEvent<HTMLButtonElement>) => {
        if (!destinationUrl) return;
        void navigator.clipboard.writeText(destinationUrl);
        toast.info(toastCopy.linkCopiedToClipboard);
        const button = event?.currentTarget;
        if (!button) return;
        button.classList.add("animate-wiggle");
        setTimeout(() => button.classList.remove("animate-wiggle"), 600);
    };

    const goBack = () => {
        void navigate({
            to: "/invite/diary/organisation",
            search: { ...search, origin, invite_method: inviteMethod },
        });
    };

    return (
        <div className="space-y-6">
            {!isLoadingOrgs ? (
                <Stepper
                    steps={getDiaryInviteSteps("qr", {
                        organisationCount: organisations.length,
                        inviteMethod,
                        copy: inviteCopy,
                    })}
                />
            ) : null}

            {atDiaryCap ? <DiaryQuotaBlockedCallout usage={diaryUsage} /> : null}

            {!hasTemplateSettings && (
                <Alert variant="destructive" className="bg-red-50 dark:bg-red-950/40 border-red-200">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <AlertDescription className="text-red-800 dark:text-red-200">
                        {inviteCopy.noTemplateSelected}{" "}
                        <button
                            type="button"
                            onClick={() => {
                                void navigate({ to: "/templates" } as never);
                            }}
                            className="font-medium underline"
                        >
                            {inviteCopy.createTemplate}
                        </button>{" "}
                        {inviteCopy.createTemplateFirst}
                    </AlertDescription>
                </Alert>
            )}

            {!hasOrganisationId ? (
                <p className="text-muted-foreground text-sm" role="status">
                    {inviteCopy.selectOrganisationToContinue}
                </p>
            ) : !hasContactId ? (
                <p className="text-muted-foreground text-sm" role="status">
                    {inviteCopy.selectContactToContinue}
                </p>
            ) : null}

            <Card className="bg-transparent dark:bg-transparent shadow-none border-0 overflow-hidden">
                <CardHeader className="space-y-0 px-0 pb-4">
                    <div className="flex sm:flex-row flex-col sm:justify-between items-start sm:items-start gap-6 sm:gap-8">
                        <div className="flex-1 space-y-6 sm:pr-6 md:pr-8 w-full min-w-0 text-left">
                            <div className="space-y-3">
                                <p className="font-semibold text-indigo-600 dark:text-indigo-400 text-xs uppercase tracking-widest">
                                    {inviteCopy.patientInvite}
                                </p>
                                <CardTitle className="font-bold text-slate-900 dark:text-slate-50 text-2xl md:text-3xl normal-case tracking-tight">
                                    {inviteCopy.diaryQrCode}
                                </CardTitle>
                                <CardDescription className="max-w-md text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                                    {inviteCopy.diaryQrDescription}
                                </CardDescription>
                                {organisation_name ? (
                                    <p className="text-slate-600 dark:text-slate-400 text-sm">
                                        {inviteCopy.organisation}: {organisation_name}
                                    </p>
                                ) : null}
                            </div>
                        </div>

                        {destinationUrl ? (
                            <div className="flex justify-center sm:justify-end mx-auto sm:mx-0 sm:pt-0.5 w-full sm:w-auto shrink-0">
                                <InviteQrMark
                                    destinationUrl={destinationUrl}
                                    title={inviteCopy.patientInvite}
                                    variant="compact"
                                    containerRef={qrWrapRef}
                                    className="mx-auto sm:mx-0"
                                />
                            </div>
                        ) : hasTemplateSettings &&
                          hasOrganisationId &&
                          hasContactId &&
                          !atDiaryCap &&
                          (isCreatingInvite || !isInviteError) ? (
                            <p className="sm:pt-2 text-muted-foreground text-sm" role="status">
                                {inviteCopy.generatingInviteLink}
                            </p>
                        ) : null}
                    </div>
                </CardHeader>

                <CardContent className="px-0 pt-2">
                    <FooterButtons
                        words1={inviteCopy.copyLink}
                        icon1={<Link2 className="w-4 h-4" aria-hidden />}
                        func1={copyLink}
                        type1="button"
                        disabled1={!destinationUrl}
                        testId1="study-invite-copy-link"
                        words2={actionCopy.back}
                        func2={goBack}
                        secondaryAction="back"
                        words3={origin === "patients" ? inviteCopy.backToPatients : templatesCopy.backToTemplates}
                        func3={() => void navigate({ to: context.rootTo })}
                        tertiaryAction="skip"
                    />
                </CardContent>
            </Card>
        </div>
    );
}
